/* Cinematic Nermiee hero: GSAP slideshow + Three.js popcorn engine.
   Config comes from window.NermieeCinema (set by the section).
   Popcorn auto-starts only after the cookie banner and newsletter popup
   are gone; a 🍿 button toggles it; shaking a phone tosses the pile. */
(function () {
  'use strict';

  var cfg = window.NermieeCinema;
  if (!cfg) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var destroyed = false;
  var rafId = null;
  var listeners = [];
  var gateTimer = null;
  var aliveCheck = null;

  function on(target, ev, fn, opts) {
    target.addEventListener(ev, fn, opts);
    listeners.push([target, ev, fn, opts]);
  }

  function init() {
    var section = document.querySelector('[data-nermiee-hero]');
    if (!section || section.dataset.nermieeInit === '1') return;
    section.dataset.nermieeInit = '1';

    initSlideshow(section);

    if (cfg.popcorn && cfg.popcorn.enabled) {
      var booted = false;
      var boot = function () {
        if (booted || destroyed) return;
        booted = true;
        try {
          initPopcorn(section);
        } catch (err) {
          var mount = document.getElementById('NermieePopcorn');
          if (mount && mount.parentNode) mount.parentNode.removeChild(mount);
        }
      };
      if (document.readyState === 'complete') {
        setTimeout(boot, 400);
      } else {
        on(window, 'load', function () { setTimeout(boot, 400); });
        setTimeout(boot, 2500); // fallback if load stalls
      }
    }

    aliveCheck = setInterval(function () {
      if (!document.body.contains(section)) {
        destroy();
      }
    }, 2000);

    on(document, 'shopify:section:unload', function (e) {
      if (e.detail && String(e.detail.sectionId) === String(cfg.sectionId)) destroy();
    });
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (rafId) cancelAnimationFrame(rafId);
    if (gateTimer) clearInterval(gateTimer);
    if (aliveCheck) clearInterval(aliveCheck);
    listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    listeners = [];
    document.body.classList.remove('nermiee-cinema');
    ['NermieePopcorn', 'NermieeShakeBtn', 'NermieeToggleBtn'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    });
  }

  /* ---------------- Slideshow ---------------- */

  function initSlideshow(section) {
    var slides = section.querySelectorAll('.nermiee-cinema-hero__slide');
    if (slides.length < 2 || !window.gsap) return;

    var g = window.gsap;
    var duration = Math.max(3, cfg.slideDuration || 5);
    var current = 0;
    var timer = null;

    function kenBurns(el) {
      if (reducedMotion) return;
      g.fromTo(el, { scale: 1.04 }, { scale: 1.12, duration: duration + 1.6, ease: 'none', overwrite: 'auto' });
    }

    function schedule() {
      if (timer) timer.kill();
      timer = g.delayedCall(duration, function () { goTo(current + 1, 1.1); });
    }

    function goTo(idx, fade) {
      if (destroyed) return;
      var nextIdx = ((idx % slides.length) + slides.length) % slides.length;
      if (nextIdx === current) { schedule(); return; }
      var prev = slides[current];
      var cur = slides[nextIdx];
      current = nextIdx;

      cur.classList.add('is-active');
      kenBurns(cur);
      g.fromTo(cur, { autoAlpha: 0 }, { autoAlpha: 1, duration: fade, ease: 'power2.inOut' });
      g.to(prev, {
        autoAlpha: 0,
        duration: fade,
        ease: 'power2.inOut',
        onComplete: function () { prev.classList.remove('is-active'); }
      });
      schedule();
    }

    kenBurns(slides[0]);
    schedule();

    // Touch: horizontal swipe changes slides (vertical scroll stays native
    // via touch-action: pan-y on the hero link; a swipe never fires click,
    // so the product link only triggers on a clean tap).
    var touchX = 0, touchY = 0, touchLive = false;
    on(section, 'touchstart', function (e) {
      if (e.touches.length === 1) {
        touchLive = true;
        touchX = e.touches[0].clientX;
        touchY = e.touches[0].clientY;
      }
    }, { passive: true });
    on(section, 'touchend', function (e) {
      if (!touchLive) return;
      touchLive = false;
      var t = e.changedTouches[0];
      var dx = t.clientX - touchX;
      var dy = t.clientY - touchY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        goTo(dx < 0 ? current + 1 : current - 1, 0.5);
      }
    }, { passive: true });
  }

  /* ---------------- Popcorn ---------------- */

  function initPopcorn(section) {
    if (!window.THREE || !window.gsap) return;
    var THREE = window.THREE;
    var mount = document.getElementById('NermieePopcorn');
    if (!mount) return;

    var W = window.innerWidth;
    var H = window.innerHeight;

    // On mobile, extend the popcorn world below the layout viewport so the
    // pile forms behind/under the browser's floating bottom navigation
    // instead of "cutting off" at a false floor above it.
    var isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    // Cover whatever browser chrome sits below the layout viewport (iOS 27's
    // bottom navigation can be large): extend by at least the measured
    // screen-vs-viewport gap so the pile always reaches the physical bottom.
    var cfgOffset = Math.max(0, cfg.popcorn.bottomOffset != null ? cfg.popcorn.bottomOffset : 200);
    var chromeGap = (window.screen && screen.height) ? Math.max(0, screen.height - window.innerHeight) : 0;
    var EXT = isTouch ? Math.max(cfgOffset, chromeGap + 60) : 0;
    var FLOOR = -EXT;

    var renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'low-power' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(W, H + EXT);
    renderer.outputEncoding = THREE.sRGBEncoding;
    mount.style.height = 'calc(100% + ' + EXT + 'px)';
    mount.appendChild(renderer.domElement);

    var scene = new THREE.Scene();
    var camera = new THREE.OrthographicCamera(0, W, H, FLOOR, -100, 100);

    var MAX = Math.max(60, cfg.popcorn.maxKernels || 220);
    var SPAWN_PER_SEC = Math.max(0.5, cfg.popcorn.spawnPerSec || 2);
    var SHAKE_SENS = Math.max(6, cfg.popcorn.shakeSensitivity || 16);
    var GRAVITY = -1100;         // lively fall
    var TERMINAL = -700;
    var COL_W = 12;
    var PACKING = 0.42;

    var running = false;         // spawning + simulation active
    var userOverride = false;    // user touched the toggle; gate stops auto-starting
    var texUrls = cfg.popcorn.textures || [];
    var meshes = [];
    var kernels = [];
    var freeSlots = [];
    var settledQueue = [];
    var pileH, pileCap;
    var dummy = new THREE.Object3D();
    var loader = new THREE.TextureLoader();
    var perMesh = Math.ceil((MAX + 40) / Math.max(1, texUrls.length));
    var needsRender = true;

    function resetPileField() {
      pileH = new Float32Array(Math.ceil(W / COL_W) + 1);
      pileCap = H * 0.16 + EXT; // pile rises from below the fold into view
    }
    resetPileField();

    function colAt(x) {
      var c = Math.floor(x / COL_W);
      if (c < 0) c = 0;
      if (c >= pileH.length) c = pileH.length - 1;
      return c;
    }

    texUrls.forEach(function (url) {
      loader.load(url, function (tex) {
        tex.encoding = THREE.sRGBEncoding;
        var aspect = tex.image.height / tex.image.width;
        var geo = new THREE.PlaneGeometry(1, 1);
        var mat = new THREE.MeshBasicMaterial({
          map: tex,
          transparent: true,
          depthWrite: false,
          alphaTest: 0.05
        });
        var mesh = new THREE.InstancedMesh(geo, mat, perMesh);
        mesh.frustumCulled = false;
        dummy.position.set(-9999, -9999, 0);
        dummy.scale.set(0.0001, 0.0001, 1);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        for (var i = 0; i < perMesh; i++) {
          mesh.setMatrixAt(i, dummy.matrix);
          freeSlots.push({ mesh: meshes.length, index: i });
        }
        mesh.instanceMatrix.needsUpdate = true;
        mesh.userData.aspect = aspect;
        scene.add(mesh);
        meshes.push(mesh);
      });
    });

    function freeKernel(k) {
      k.dead = true;
      var mesh = meshes[k.meshIdx];
      if (mesh) {
        dummy.position.set(-9999, -9999, 0);
        dummy.scale.set(0.0001, 0.0001, 1);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        mesh.setMatrixAt(k.index, dummy.matrix);
        mesh.instanceMatrix.needsUpdate = true;
      }
      freeSlots.push({ mesh: k.meshIdx, index: k.index });
    }

    function spawn(fromTop, burstVel) {
      if (!meshes.length) return;
      var slot = freeSlots.pop();
      if (!slot) {
        // recycle the oldest settled kernel so fresh ones keep falling
        var old = settledQueue.shift();
        if (!old) return;
        pileH[old.col] = Math.max(0, pileH[old.col] - old.contrib);
        old.dead = true;
        slot = { mesh: old.meshIdx, index: old.index };
        kernels.splice(kernels.indexOf(old), 1);
      }
      var meshIdx = slot.mesh >= meshes.length ? 0 : slot.mesh;
      var size = 26 + Math.random() * 32;
      kernels.push({
        meshIdx: meshIdx,
        index: slot.index,
        size: size,
        x: Math.random() * W,
        y: fromTop ? H + 60 + Math.random() * 160 : H * (0.3 + Math.random() * 0.4),
        vx: (Math.random() - 0.5) * (burstVel ? 380 : 40),
        vy: burstVel ? 140 + Math.random() * 320 : 0,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 3,
        drift: Math.random() * Math.PI * 2,
        settled: false,
        dying: false,
        col: 0,
        contrib: 0,
        dead: false
      });
      needsRender = true;
    }

    function settle(k) {
      k.settled = true;
      k.vx = 0; k.vy = 0; k.vr = 0;
      k.col = colAt(k.x);
      // Hidden depth below the fold fills fast: the first kernel in a column
      // seeds a baseline so the pile forms behind the browser nav and rises
      // into view instead of needing hundreds of unseen kernels.
      if (EXT > 0 && pileH[k.col] < EXT * 0.5) {
        pileH[k.col] = EXT * (0.55 + Math.random() * 0.3);
        k.y = FLOOR + pileH[k.col] + k.size * 0.2;
      }
      var add = k.size * PACKING;
      if (pileH[k.col] < pileCap) {
        k.contrib = add;
        pileH[k.col] += add;
      } else {
        k.contrib = 0;
      }
      settledQueue.push(k);
    }

    function toss(extra) {
      for (var i = 0; i < kernels.length; i++) {
        var k = kernels[i];
        if (k.dying) continue;
        k.settled = false;
        k.contrib = 0;
        k.vy = 320 + Math.random() * 480;
        k.vx = (Math.random() - 0.5) * 520;
        k.vr = (Math.random() - 0.5) * 7;
      }
      settledQueue.length = 0;
      resetPileField();
      var burst = Math.min(extra, MAX - kernels.length);
      for (var b = 0; b < burst; b++) spawn(true, true);
      needsRender = true;
    }

    /* --- start/stop --- */

    var toggleBtn = document.getElementById('NermieeToggleBtn');
    var shakeBtn = document.getElementById('NermieeShakeBtn');

    // Seat the toggle in the header icon row, right beside the cart icon,
    // so it vertically aligns with it. Falls back to fixed positioning.
    if (toggleBtn) {
      var cartLink = document.querySelector('.site-header .js-drawer-open-cart');
      if (cartLink && cartLink.parentNode) {
        toggleBtn.classList.add('nermiee-toggle--header');
        cartLink.parentNode.insertBefore(toggleBtn, cartLink);
      }
    }

    function syncButtons() {
      if (toggleBtn) {
        toggleBtn.classList.add('is-ready');
        toggleBtn.classList.toggle('is-off', !running);
        toggleBtn.setAttribute('aria-pressed', running ? 'true' : 'false');
      }
      if (shakeBtn) {
        var wantPill = running && needsMotionPermission && !motionGranted;
        shakeBtn.classList.toggle('is-visible', !!wantPill);
      }
    }

    function start() {
      if (running || destroyed) return;
      running = true;
      syncButtons();
    }

    function stop() {
      if (!running) return;
      running = false;
      for (var i = 0; i < kernels.length; i++) kernels[i].dying = true;
      settledQueue.length = 0;
      resetPileField();
      syncButtons();
    }

    function prefersOff() {
      try { return localStorage.getItem('nermieePopcornOff') === '1'; } catch (e) { return false; }
    }

    if (toggleBtn) {
      on(toggleBtn, 'click', function () {
        userOverride = true;
        if (running) {
          stop();
          try { localStorage.setItem('nermieePopcornOff', '1'); } catch (e) {}
        } else {
          start();
          try { localStorage.removeItem('nermieePopcornOff'); } catch (e) {}
        }
      });
    }

    /* Auto-start on page load — unless the visitor prefers reduced motion
       or previously switched the popcorn off (their choice persists). */
    if (!reducedMotion && !prefersOff()) {
      start();
    } else {
      syncButtons();
    }

    /* --- shake detection --- */
    var lastMag = null;
    var lastShakeAt = 0;
    var motionGranted = false;
    var needsMotionPermission = false;

    function onMotion(e) {
      if (!running) return;
      var a = e.accelerationIncludingGravity;
      if (!a || a.x == null) return;
      var mag = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
      if (lastMag !== null) {
        var delta = Math.abs(mag - lastMag);
        var now = Date.now();
        if (delta > SHAKE_SENS && now - lastShakeAt > 900) {
          lastShakeAt = now;
          toss(16);
        }
      }
      lastMag = mag;
    }

    if (isTouch && typeof DeviceMotionEvent !== 'undefined') {
      if (typeof DeviceMotionEvent.requestPermission === 'function') {
        needsMotionPermission = true;
        if (shakeBtn) {
          on(shakeBtn, 'click', function () {
            DeviceMotionEvent.requestPermission().then(function (state) {
              motionGranted = state === 'granted';
              if (motionGranted) {
                on(window, 'devicemotion', onMotion);
                if (running) toss(12);
              }
              syncButtons();
            }).catch(function () {
              motionGranted = false;
              needsMotionPermission = false;
              syncButtons();
            });
          });
        }
      } else {
        motionGranted = true;
        on(window, 'devicemotion', onMotion);
      }
    }
    syncButtons(); // re-sync now that motion-permission state is known

    /* --- desktop mouse wind --- */
    var mouseX = -9999, mouseY = -9999, mouseVX = 0;
    var lastMouseX = null, lastMouseT = 0;
    if (!isTouch) {
      on(window, 'mousemove', function (e) {
        var now = performance.now();
        mouseX = e.clientX;
        mouseY = H - e.clientY;
        if (lastMouseX !== null && now > lastMouseT) {
          mouseVX = (e.clientX - lastMouseX) / ((now - lastMouseT) / 1000);
          mouseVX = Math.max(-1200, Math.min(1200, mouseVX));
        }
        lastMouseX = e.clientX;
        lastMouseT = now;
      });
    }

    /* --- resize --- */
    var resizeT = null;
    on(window, 'resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        W = window.innerWidth;
        H = window.innerHeight;
        renderer.setSize(W, H + EXT);
        camera.right = W;
        camera.top = H;
        camera.updateProjectionMatrix();
        resetPileField();
        settledQueue.length = 0;
        for (var i = 0; i < kernels.length; i++) {
          var rk = kernels[i];
          rk.x = Math.min(rk.x, W - 4);
          if (rk.settled) { rk.settled = false; rk.contrib = 0; rk.vy = 40; }
        }
        needsRender = true;
      }, 250);
    });

    /* --- main loop --- */
    var spawnAcc = 0;
    var lastT = performance.now();

    function frame(now) {
      if (destroyed) return;
      rafId = requestAnimationFrame(frame);
      var dt = Math.min(0.05, (now - lastT) / 1000);
      lastT = now;
      if (document.hidden || !meshes.length) return;
      if (!running && !kernels.length && !needsRender) return; // idle

      if (running && kernels.length < MAX) {
        spawnAcc += dt * SPAWN_PER_SEC;
        while (spawnAcc >= 1) {
          spawnAcc -= 1;
          spawn(true, false);
        }
      }

      var anyMotion = false;
      for (var i = kernels.length - 1; i >= 0; i--) {
        var k = kernels[i];
        if (k.dead) { kernels.splice(i, 1); continue; }

        if (k.dying) {
          k.size *= Math.max(0, 1 - 4 * dt);
          anyMotion = true;
          if (k.size < 2) {
            freeKernel(k);
            kernels.splice(i, 1);
          }
          continue;
        }
        if (k.settled) continue;

        anyMotion = true;
        k.vy += GRAVITY * dt;
        if (k.vy < TERMINAL) k.vy = TERMINAL;
        k.drift += dt * 1.6;
        k.vx += Math.sin(k.drift) * 16 * dt;
        k.vx *= (1 - 0.4 * dt);

        if (!isTouch && mouseX > -999) {
          var dx = k.x - mouseX;
          var dy = k.y - mouseY;
          if (dx * dx + dy * dy < 19600) {
            k.vx += mouseVX * 0.12 * dt * 8;
          }
        }

        k.x += k.vx * dt;
        k.y += k.vy * dt;
        k.rot += k.vr * dt;

        var r = k.size * 0.4;
        if (k.x < r) { k.x = r; k.vx *= -0.5; }
        if (k.x > W - r) { k.x = W - r; k.vx *= -0.5; }

        // land on the pile (which starts below the browser nav): one small
        // bounce if arriving fast, then settle and stack
        var floor = FLOOR + pileH[colAt(k.x)] + r;
        if (k.y <= floor && k.vy <= 0) {
          k.y = floor;
          if (Math.abs(k.vy) > 260) {
            k.vy = Math.abs(k.vy) * 0.3;
            k.vx *= 0.7;
          } else {
            settle(k);
          }
        }
      }

      if (anyMotion || needsRender) {
        for (var j = 0; j < kernels.length; j++) {
          var kk = kernels[j];
          if (kk.dead) continue;
          var mesh = meshes[kk.meshIdx];
          if (!mesh) continue;
          var aspect = mesh.userData.aspect || 1;
          dummy.position.set(kk.x, kk.y, 0);
          dummy.rotation.set(0, 0, kk.rot);
          dummy.scale.set(Math.max(kk.size, 0.001), Math.max(kk.size * aspect, 0.001), 1);
          dummy.updateMatrix();
          mesh.setMatrixAt(kk.index, dummy.matrix);
          mesh.instanceMatrix.needsUpdate = true;
        }
        renderer.render(scene, camera);
        needsRender = false;
      }
    }
    rafId = requestAnimationFrame(frame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () {
    destroyed = false;
    init();
  });
})();
